import { useState } from 'react';
import type { Newsletter } from '@shared/types';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { MONTH_NAMES, dateLabel } from '../lib/format';
import { Banner, Field, LoadingPage, Markdown, Tag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

const COMMUNITY_KEY = 'hoa-community-name';

export function NewsletterPage() {
  const { notify, fail } = useToast();
  const state = useAsync(async () => api.newsletters(), []);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [communityName, setCommunityName] = useState(
    () => localStorage.getItem(COMMUNITY_KEY) ?? 'Our Neighborhood',
  );
  const [extraNotes, setExtraNotes] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [engine, setEngine] = useState<'gemini' | 'template' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [preview, setPreview] = useState(true);
  const [busy, setBusy] = useState<'generate' | 'save' | 'publish' | null>(null);

  const aiEngine = state.data?.aiEngine ?? 'template';
  const newsletters = state.data?.newsletters ?? [];

  async function generate() {
    setBusy('generate');
    localStorage.setItem(COMMUNITY_KEY, communityName);
    try {
      const result = await api.newsletterGenerate({
        year,
        month,
        communityName,
        extraNotes,
        engine: aiEngine === 'gemini' ? 'gemini' : 'template',
      });
      setBody(result.draft);
      setTitle(result.title);
      setEngine(result.engine);
      notify(
        `Drafted from ${result.context.issueCount} request${
          result.context.issueCount === 1 ? '' : 's'
        }, ${result.context.eventCount} event${result.context.eventCount === 1 ? '' : 's'}${
          result.context.hasFinance ? ' and the latest financials' : ''
        }.`,
        'success',
      );
    } catch (error) {
      fail(error, 'The draft was not generated.');
    } finally {
      setBusy(null);
    }
  }

  async function save(status: 'draft' | 'published') {
    if (!title.trim() || !body.trim()) {
      notify('Generate or write the newsletter first.', 'error');
      return;
    }
    setBusy(status === 'draft' ? 'save' : 'publish');
    try {
      const payload = { year, month, title, body, status, engine };
      const result = editingId
        ? await api.newsletterUpdate(editingId, payload)
        : await api.newsletterSave(payload);
      setEditingId(result.newsletter.id);
      await state.reload();
      notify(status === 'published' ? 'Newsletter published.' : 'Draft saved.', 'success');
    } catch (error) {
      fail(error, 'The newsletter was not saved.');
    } finally {
      setBusy(null);
    }
  }

  function load(newsletter: Newsletter) {
    setEditingId(newsletter.id);
    setYear(newsletter.year);
    setMonth(newsletter.month);
    setTitle(newsletter.title);
    setBody(newsletter.body);
    setEngine(newsletter.engine);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function remove(newsletter: Newsletter) {
    if (!confirm(`Delete "${newsletter.title}"?`)) return;
    try {
      await api.newsletterDelete(newsletter.id);
      if (editingId === newsletter.id) setEditingId(null);
      await state.reload();
      notify('Deleted.');
    } catch (error) {
      fail(error, 'It was not deleted.');
    }
  }

  async function copy() {
    await navigator.clipboard.writeText(body);
    notify('Copied to the clipboard.', 'success');
  }

  if (state.loading && !state.data) return <LoadingPage label="Loading newsletters…" />;

  return (
    <div>
      <PageHeader
        title="Monthly newsletter"
        lede="Pulls this month's complaints, architectural decisions, upcoming events and the latest financial snapshot into a draft. Edit anything before you publish."
      />

      {aiEngine === 'template' && (
        <Banner kind="warn">
          No AI provider key is set, so drafts use the built-in template writer. A Super Admin can
          add a key in Super Admin → Settings to have AI write it.
        </Banner>
      )}

      <div className="card">
        <div className="grid two">
          <Field label="Community name">
            <input
              value={communityName}
              onChange={(event) => setCommunityName(event.target.value)}
            />
          </Field>
          <Field label="Month">
            <select value={month} onChange={(event) => setMonth(Number(event.target.value))}>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Year">
            <input
              type="number"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
            />
          </Field>
        </div>

        <Field
          label="Anything else to include"
          hint="Reminders, thank-yous, policy changes — the draft will weave these in."
        >
          <textarea
            rows={3}
            value={extraNotes}
            onChange={(event) => setExtraNotes(event.target.value)}
            placeholder="Trash pickup moves to Wednesdays starting the 14th. Thanks to the Delgados for organizing the cleanup."
          />
        </Field>

        <div className="btn-row">
          <button type="button" className="btn" disabled={busy !== null} onClick={() => void generate()}>
            {busy === 'generate' ? 'Drafting…' : 'Draft from this month'}
          </button>
          {engine && <Tag tone="info">{engine === 'gemini' ? 'Written by AI' : 'Template draft'}</Tag>}
        </div>
      </div>

      {(body || title) && (
        <div className="card">
          <Field label="Title">
            <input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>

          <div className="row between" style={{ marginBottom: 8 }}>
            <span className="label-text">Body (Markdown)</span>
            <button type="button" className="btn ghost tiny" onClick={() => setPreview((on) => !on)}>
              {preview ? 'Edit' : 'Preview'}
            </button>
          </div>

          {preview ? (
            <div
              style={{
                border: '1px solid var(--bd)',
                borderRadius: 18,
                padding: '16px 20px',
                background: 'var(--bg)',
              }}
            >
              <Markdown source={body} />
            </div>
          ) : (
            <textarea rows={22} value={body} onChange={(event) => setBody(event.target.value)} />
          )}

          <div className="btn-row">
            <button type="button" className="btn s" disabled={busy !== null} onClick={() => void save('draft')}>
              {busy === 'save' ? 'Saving…' : 'Save draft'}
            </button>
            <button type="button" className="btn" disabled={busy !== null} onClick={() => void save('published')}>
              {busy === 'publish' ? 'Publishing…' : 'Publish'}
            </button>
            <button type="button" className="btn ghost" onClick={() => void copy()}>
              Copy text
            </button>
            {editingId && (
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setEditingId(null);
                  setTitle('');
                  setBody('');
                  setEngine(null);
                }}
              >
                Start fresh
              </button>
            )}
          </div>
        </div>
      )}

      <h2>Past issues</h2>
      {newsletters.length === 0 ? (
        <div className="card">
          <p className="mut">Nothing published yet.</p>
        </div>
      ) : (
        newsletters.map((newsletter) => (
          <div key={newsletter.id} className="card tight">
            <div className="card-head">
              <h4>{newsletter.title}</h4>
              <div className="tag-row">
                <Tag tone={newsletter.status === 'published' ? 'ok' : 'pend'}>{newsletter.status}</Tag>
                {newsletter.engine === 'gemini' && <Tag tone="info">AI draft</Tag>}
              </div>
            </div>
            <div className="mut">
              {MONTH_NAMES[newsletter.month - 1]} {newsletter.year} · {newsletter.createdByName} ·{' '}
              {newsletter.publishedAt
                ? `published ${dateLabel(newsletter.publishedAt)}`
                : `updated ${dateLabel(newsletter.updatedAt)}`}
            </div>
            <div className="btn-row">
              <button type="button" className="btn s tiny" onClick={() => load(newsletter)}>
                Open
              </button>
              <button type="button" className="btn ghost tiny" onClick={() => void remove(newsletter)}>
                Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
